/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography,
    IconButton,
    Chip,
    Avatar,
    Divider,
    Button,
    TextField,
    MenuItem,
    Tooltip,
    Autocomplete,
    CircularProgress as MuiCircularProgress,
    Select,
    Popover,
    ClickAwayListener,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { dispatch, useSelector } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { getTask, getComments, updateTask, setCurrentTask } from 'src/redux/slices/taskSlice';
import { getPracticeUserList } from 'src/redux/slices/practiceUser';
// V1 surfaced lead-scoped activity (LeadNote) as a "Comments" list on the
// Reminder sidebar — operators used it to log call outcomes like
// "UNABLE_TO_CONTACT" and the comment stayed visible across every reminder
// for that lead. V2 introduced TaskComment (task-scoped) but stopped
// reading lead notes in the task sidebar, so V1-era comment activity
// "disappeared" after migration even though the LeadNote rows still
// exist on the lead. We re-attach the lead-notes fetch here and merge
// the two streams in TaskDetailComments below so V1→V2 has visible parity.
import { getLeadNotes } from 'src/redux/slices/lead/coreThunks';
import { PRIORITY_COLORS, TASK_PRIORITY } from 'src/constants/TaskConstants';
import { error, warning, grey, primary } from 'src/theme/palette';
import LeadActionZone from 'src/components/dashboardSidebars/LeadActionZone';
import AssignedToLead from 'src/components/AssignedToLead';
import { CHECKLIST_ITEM_TYPE, CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import {
    accordionSectionStyle,
    notesLabelStyle,
} from 'src/styles/taskStyles';
import { useNavigate } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { getTaskPauseState } from 'src/utils/taskPauseState';
import copyDebugId from 'src/utils/copyDebugId';
import { getJourneyLabel, getJourneyDot } from 'src/utils/leadJourney';
import { localStorageRemoveItem } from 'src/utils/storage-available';
import ContactLeadPopupRouter from 'src/components/fullScreenPopUps/ContactLeadPopupRouter';
import OpenAttendedPopup from 'src/components/fullScreenPopUps/OpenAttendedPopup';
import OpenUnAttendedPopup from 'src/components/fullScreenPopUps/OpenUnAttendedPopup';
import CompletedTreatmentHub from 'src/pages/dashboard/tables/AllLeadTable/CompletedTreatmentHub';
import { resolveLeadActionPopup, normalizeStatus, ACTION, POPUP } from 'src/utils/leadActionPopupResolver';
import { FollowUp, AppointmentPage } from 'src/sections/@dashboard/lead';
import { TreatmentAndAppointment } from 'src/sections/@dashboard/general/app';
import { UnableToContact } from 'src/components/failedToAttendTabs';
import { CreateLeadHeaderData } from 'src/constants/LeadDetailConst';
import {
    tableLead,
    updateFollowUpLead,
    updateSteps,
    CompleteTreatment,
} from 'src/redux/slices/lead';
import { followUpSlice, getAppointment } from 'src/redux/slices/lead/appointmentThunks';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import { setLoader } from 'src/redux/slices/loader';
import { getActivities } from 'src/redux/slices/activity';
import { initializeFacebookAuthToken } from 'src/redux/slices/whatsapp';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { interpolateTaskMergeTags } from 'src/utils/taskMergeTags';
import { piiProps } from 'src/components/DemoBlur';
import TaskAttachments from './TaskAttachments';
import TaskTimeTracker from './TaskTimeTracker';
import TaskDetailChecklist from './TaskDetailChecklist';
import TaskDetailComments from './TaskDetailComments';
import { pickActiveTreatments } from './_shared/treatmentBindHelpers';
import { broadcastTaskCompleting, TASK_EXIT_MS } from './_shared/taskExitAnimation';

// -----------------------------------------------
// TaskBodyView (#226) — shared body region rendered by both TaskSlideout
// (sidebar/modal chrome) and TaskDetailPage (full-page chrome). Owns the
// status banner → title → lead block → Properties grid (Details overline)
// → Notes → Comments → Attachments → Time tracking → Debug ID footer.
// Polish parity (#185-#223) lives here so all entry points stay in lockstep.
// -----------------------------------------------

function DebugIdBadge({ taskId, leadId, practiceId }) {
    const [copied, setCopied] = useState(false);
    const shortId = String(taskId).slice(0, 8);
    const handleCopy = (e) => {
        e.stopPropagation();
        const url = `${window.location.origin}/dashboard/practice/${practiceId}/tasks-v2/${taskId}`;
        copyDebugId({ task: taskId, lead: leadId || '', url }).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <Tooltip title={copied ? 'Copied!' : `Full UUID + lead + URL copied`} placement="right">
            <Box
                component="span"
                onClick={handleCopy}
                sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, cursor: 'pointer',
                    color: copied ? 'success.main' : 'text.disabled',
                    fontFamily: 'monospace', fontSize: '0.68rem', lineHeight: 1,
                    '&:hover': { color: 'primary.main' },
                    '&:hover .debug-copy-icon': { opacity: 1 },
                }}
            >
                ID: {shortId}
                <Iconify className="debug-copy-icon" icon="eva:copy-outline" width={12} sx={{ opacity: 0.5, transition: 'opacity 0.15s' }} />
            </Box>
        </Tooltip>
    );
}

DebugIdBadge.propTypes = {
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
const formatDue = (dueAt) => {
    if (!dueAt) return null;
    const parsed = parseApiDate(dueAt);
    if (!parsed || !parsed.isValid()) return null;
    const d = parsed.toDate();
    return d.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Lead-journey context dates shown in the slideout (read-only). Lead created
// (`created_at`) arrives as ISO; treatment state_transition_logs timestamps are
// 'DD-MM-YYYY HH:mm:ss'. Parse defensively and render date-only.
const formatJourneyDate = (val) => {
    if (!val) return null;
    let m = moment.isMoment(val) ? val : moment(val, moment.ISO_8601, true);
    if (!m.isValid()) m = moment(val, 'DD-MM-YYYY HH:mm:ss');
    if (!m.isValid()) m = moment(val);
    return m.isValid() ? m.format('ddd DD MMM YYYY') : null;
};

// Latest timestamp for a given state-transition (e.g. 'to_consultation_attended',
// 'to_treatment_started') across a treatment's state_transition_logs.
const findTransitionDate = (logs, transition) => {
    const matches = (logs || []).filter((el) => el?.transition === transition && el?.timestamp);
    if (!matches.length) return null;
    const m = moment.max(matches.map((el) => moment(el.timestamp, 'DD-MM-YYYY HH:mm:ss')));
    return m && m.isValid() ? m : null;
};

export default function TaskBodyView({
    task,
    lead,
    leadTreatment,
    practiceId,
    taskId,
    mode,
    open,
    onTaskUpdated,
    onClose,
    nowTick,
    pushItemUndo,
}) {
    const navigate = useNavigate();
    // Comments live on the same `task` slice; render whatever the parent fetched.
    const comments = useSelector((state) => state.task?.comments || null);
    // V1-era lead-scoped notes (LeadNote) — see import comment above. We
    // read them off the ``lead`` slice so we don't duplicate state, and
    // expose them to TaskDetailComments below merged with TaskComment.
    const leadNotes = useSelector((state) => state.lead?.leadNotes || []);
    // Superadmin-only debug surfaces (template link / V1-migrated source).
    // Reads ``is_superuser`` off the cached auth user; same pattern as
    // ``TemplateLibrary.jsx:489-490``. Depends on PR #5190 (BE) exposing
    // ``is_superuser`` on ``/auth/user/`` — already deployed to staging.
    const { user } = useAuth();
    const isSuperuser = !!user?.is_superuser;
    const sourceTemplateId =
        task?.template?.id || task?.template_id || task?.metadata?.template_id || null;
    const migratedFromReminderId = task?.metadata?.migrated_from_reminder_id || null;

    // ---- local body UI state (was previously inlined in TaskSlideout) ----
    // V2 stores user-facing reminder notes in two places depending on origin:
    //   * Native V2 tasks: ``Task.description`` (the canonical "Notes" column,
    //     written by ``handleSaveNotes`` below).
    //   * V1-migrated tasks: ``Task.note`` (the V1-promoted column — see
    //     ``apps/tasks_migration/transform.py:417`` + the field's docstring at
    //     ``apps/tasks/models.py:208-215``). The migration deliberately keeps
    //     V1.note → V2.note so the parity check in
    //     ``apps/tasks_migration/canonical.py:348`` round-trips byte-identically.
    //
    // Reading ``description`` only (the prior behaviour) caused V1-migrated
    // tasks to show an empty Notes section even when V1 had a populated
    // ``Reminder.note`` — exactly the "missing Reminder Notes" bug reported
    // on 2026-05-13 (V1 had "call him asap now"; V2 Notes was blank). Prefer
    // ``description`` when non-empty (V2-native authoring already in scope),
    // fall back to ``note`` (V1-migrated content) so the operator sees the
    // content their team typed. On save we still write to ``description`` —
    // the canonical V2 column — which preserves the V1 ``note`` shadow for
    // parity until V1 sunset.
    const initialNotes = task?.description || task?.note || '';
    const [notes, setNotes] = useState(initialNotes);
    const [notesDirty, setNotesDirty] = useState(false);
    const [savingNotes, setSavingNotes] = useState(false);
    const [notesEditing, setNotesEditing] = useState(false);

    // Inline-rename state — ad-hoc tasks only (template_id == null).
    // Handlers are declared after handlePatchField to avoid TDZ.
    const [renamingTitle, setRenamingTitle] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const renameTitleRef = useRef(null);

    const isAdHocTask = !task?.template_id && !task?.template?.id;

    // Properties-grid editor state
    const [treatmentRowAnchor, setTreatmentRowAnchor] = useState(null);
    const [dueDateAnchor, setDueDateAnchor] = useState(null);
    const [dueDatePicking, setDueDatePicking] = useState(false);
    const [savingField, setSavingField] = useState(null);
    const [fieldError, setFieldError] = useState(null);
    const [unpausingChecklist, setUnpausingChecklist] = useState(false);

    // Assignee + dependency editing
    const [depPickerOpen, setDepPickerOpen] = useState(false);
    const [depOptions, setDepOptions] = useState([]);
    const [depSearchLoading, setDepSearchLoading] = useState(false);

    const [autoBindLabel, setAutoBindLabel] = useState(null);
    const autoBindAttemptedRef = useRef(false);

    // #318 — Follow-up popup state (mirrors V1 TaskReminder pattern)
    const inputRef = useRef();
    const [rowLead, setRowLead] = useState(null);
    const [validStep, setValidStep] = useState(false);
    const [openContactLead, setOpenContactLead] = useState(false);
    const [openAttended, setOpenAttended] = useState(false);
    const [openUnAttended, setOpenUnAttended] = useState(false);
    // Treatment-in-progress hub — opened for TREATMENT_STARTED leads.
    const [openTreatmentHub, setOpenTreatmentHub] = useState(false);
    const [hubRow, setHubRow] = useState(null);
    // Drives the hub's Complete button spinner/disabled state — guards the
    // irreversible treatment_closed POST against double-submit (mirrors NewLead).
    const isLoader = useSelector((state) => state.loaderReducer.isLoader);
    // V1 selected state.lead?.contactLead here to toggle between Contact /
    // Activities steps in the OpenContactLeadPopup wizard. V2's TaskBodyView
    // has its own TaskDetailComments rendering, so we don't port that branch.
    // Selector intentionally dropped — keeping the BASE_STEPS Contact-only.
    const BookingAttendance = useSelector((state) => state.lead?.BookingAttendance);
    // Practice user list — already fetched by the getPracticeUserList dispatch below.
    // Used to resolve a profile_picture for task.created_by by matching user.id.
    const practiceUserResults = useSelector((state) => state.practiceUser?.practiceUser?.results || []);

    // #318 review-fix-1 — wrap BASE_STEPS in useMemo so the same step array
    // (and the same <FollowUp> element + ref) is reused across renders.
    // Without useMemo, every render created a fresh <FollowUp> with stale
    // closures, breaking the initial popup open.
    const BASE_STEPS = useMemo(
        () => [{
            label: 'Contact',
            component: (
                <FollowUp
                    ref={inputRef}
                    handleNext={(d) => setValidStep(!!d)}
                    handleClose={() => {
                        setOpenContactLead(false);
                        dispatch(updateSteps(null));
                        dispatch(updateFollowUpLead(null));
                    }}
                />
            ),
        }],
        // inputRef + setters are stable across renders; recompute only on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const [leadStep, setLeadStep] = useState(BASE_STEPS);
    // Diagnostic-only: launch-site label for the contact-lead popup debug box
    // (superadmin only). Captures the effectiveStatus that drove the route so we
    // can see why the popup opened on a given outcome tab.
    const [contactLeadDebugSource, setContactLeadDebugSource] = useState('task-detail:LeadActionZone');

    // Refresh the practiceUser list whenever the practice changes so the
    // assignee picker (AssignedToLead via usePracticeStaff) doesn't show
    // stale users from a previously-viewed practice — operator reported
    // a 400 ("practice_user 1048 is not a member of this practice")
    // because Redux still held practice 92's users while the URL was on
    // practice 19.
    useEffect(() => {
        if (practiceId) dispatch(getPracticeUserList(practiceId));
    }, [practiceId]);

    useEffect(() => {
        if (!BookingAttendance) {
            setLeadStep(BASE_STEPS);
            return;
        }
        // Append step 2 matching the chosen outcome, faithfully mirroring V1
        // OverView.jsx non-PMS/non-Dentally branch (OverView.jsx:839–850).
        // Each outcome routes to its OWN screen:
        //   - 'Booking Details'      (Book Appointment) → TreatmentAndAppointment (booking form)
        //   - 'Unable to contact'    (No Answer)        → UnableToContact
        //   - 'Appointment Not Booked' (Spoke) / else   → AppointmentPage (spoke / follow-up reminder)
        // Activities branch not ported — V2 uses TaskDetailComments.
        let step2Component;
        if (BookingAttendance === 'Booking Details') {
            step2Component = <TreatmentAndAppointment ref={inputRef} type={'followUp'} handleNext={(d) => setValidStep(!!d)} isOverview />;
        } else if (BookingAttendance === 'Unable to contact') {
            step2Component = <UnableToContact ref={inputRef} handleNext={(d) => setValidStep(!!d)} isOverview completeFlow={handleFollowUpPopupClose} />;
        } else {
            step2Component = <AppointmentPage ref={inputRef} handleNext={(d) => setValidStep(!!d)} />;
        }
        setLeadStep([...BASE_STEPS, { label: BookingAttendance, component: step2Component }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [BookingAttendance, BASE_STEPS]);

    // #318 review-fix-3 — sync rowLead → state.lead.tableRowLead so V1
    // popups can read it, BUT also CLEAR the global on close so stale state
    // doesn't leak across slideout sessions (the cause of the popup
    // "auto-open" the user reported earlier).
    useEffect(() => {
        if (rowLead) dispatch(tableLead(rowLead));
        // No-op cleanup here — explicit reset happens in handleFollowUpPopupClose
        // and on TaskBodyView unmount (effect below).
    }, [rowLead]);

    // Component-unmount cleanup: reset all redux + local follow-up state so
    // re-opening any task slideout starts from a clean slate.
    useEffect(() => () => {
        dispatch(tableLead(null));
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(updateFollowUpLead(null));
        dispatch(updateSteps(null));
    }, []);

    // Sync notes when task changes. Mirrors the ``initialNotes`` resolution
    // above — prefer V2-native ``description``, fall back to V1-migrated
    // ``note`` so the Notes section is populated for migrated tasks.
    useEffect(() => {
        if (task && task.id === taskId) {
            setNotes(task.description || task.note || '');
            setNotesDirty(false);
        }
    }, [task?.id, taskId, task?.description, task?.note]);

    // Pull lead-scoped notes whenever the task's lead is known. V1's
    // sidebar fetched these to render its "Comments (n)" section; V2
    // dropped the fetch when it introduced TaskComment, which is why
    // V1-era comments like the "UNABLE_TO_CONTACT" the user logged on
    // 2026-05-10 vanished from the V2 sidebar despite the LeadNote row
    // still living in the DB. We refetch here on every task change so
    // re-opening a different task in the slideout doesn't show the
    // previous task's lead notes.
    const leadIdForNotes = task?.lead?.id || null;
    useEffect(() => {
        if (leadIdForNotes && practiceId) {
            dispatch(getLeadNotes(leadIdForNotes, practiceId));
        }
    }, [leadIdForNotes, practiceId]);

    // (Removed) per-taskId auto-bind reset effect — caused the V2 sidebar
    // reload loop when the BE silently no-ops the PATCH lead_treatment call.
    // Auto-bind is now a one-shot per slideout mount; reopening a different
    // task naturally remounts the component and reinitialises the ref.

    // ---- derived data ----
    const checklist = task?.checklist_items || task?.checklist || [];
    // Map ``Task.assignments`` (the V2 shape) to PracticeUser objects —
    // NOT bare User objects. AssignedToLead writes the selection back via
    // `handleSaveAssignees({assignees: [{practice_user_id: pu.id}]})` and
    // the BE expects `practice_user_id` to be a PracticeUser.id. The old
    // shape used `a.practice_user.user` (the nested User) which leaked the
    // User.id into the picker state → BE 400 "practice_user X is not a
    // member of this practice" whenever the operator toggled an existing
    // assignee. (2026-05-26 operator report on /tasks-v2 to-do tab.)
    //
    // _assignmentRole preserved on the result so the primary-first sort
    // and visual badge still work.
    const assignees = (task?.assignments || [])
        .map((a) => {
            // Prefer the embedded practice_user when present (canonical V2
            // shape). Fall back to building one from a bare User payload so
            // we don't break legacy responses that pre-date the assignments
            // table being denormalised.
            const pu = a.practice_user;
            if (pu && typeof pu === 'object') {
                return { ...pu, _assignmentRole: a.role || null };
            }
            const user = a.user || a;
            if (!user || typeof user !== 'object') return user;
            // Legacy fallback: wrap the User in a PracticeUser-shaped envelope.
            // ``id`` stays User-id here only because we have no PracticeUser
            // FK to read; AssignedToLead's toggle will still match against
            // usePracticeStaff (which loads from Redux) on the canonical
            // PracticeUser.id once the user re-fetches.
            return { id: user.id, user, _assignmentRole: a.role || null, _isLegacyShape: true };
        })
        // Stable sort: primary first, then observers. Falsy ``role`` sorts
        // last so legacy data without role info doesn't displace primaries.
        .sort((a, b) => {
            const rank = (r) => (r === 'primary' ? 0 : r === 'observer' ? 1 : 2);
            return rank(a?._assignmentRole) - rank(b?._assignmentRole);
        });
    // ISS-006 — assignment provenance. A staff member who found four
    // bulk-assigned tasks on her board had no way to see who put them there
    // or when, and reported it as the system auto-assigning work to her.
    // Derived from the raw ``task.assignments`` rows (NOT ``assignees``)
    // because the actor + timestamp live on the assignment row, not on the
    // PracticeUser. ``assigned_by === null`` means an automated process
    // (lifecycle receiver / beat job / migration) wrote the row — rendered as
    // "Assigned automatically" rather than as a blank actor.
    const assignmentProvenance = (task?.assignments || []).map((a, i) => {
        const pu = a?.practice_user;
        const puUser = pu?.user || null;
        const assigneeName = pu?.full_name
            || `${puUser?.first_name || ''} ${puUser?.last_name || ''}`.trim()
            || puUser?.email
            || '';
        const by = a?.assigned_by || null;
        // ``by`` is a real User row when present, so a blank display name only
        // happens on malformed payloads — fall back to a neutral label rather
        // than rendering an avatar next to empty text.
        const byName = by
            ? (`${by.first_name || ''} ${by.last_name || ''}`.trim() || by.email || 'A colleague')
            : '';
        return {
            key: a?.id || `${pu?.id ?? 'na'}-${i}`,
            assigneeName,
            by,
            byName,
            assignedAt: a?.assigned_at || null,
            // Canonical V2 assignment rows carry practice_user / role /
            // assigned_at. A bare-User legacy payload carries none of them, so
            // a missing ``assigned_by`` there means "unknown", NOT "automated" —
            // drop those rows rather than make a false claim.
            hasProvenance: !!(a && (a.practice_user || a.role || a.assigned_at || a.assigned_by)),
        };
    }).filter((p) => p.hasProvenance);
    const priority = task?.priority || 'normal';
    const status = task?.status || 'pending';
    const isCompleted = status === 'completed';
    const isArchived = status === 'archived';
    const pauseState = getTaskPauseState(task);
    const isPaused = pauseState.isPaused;
    const pausedChecklistItemId = pauseState.isChecklistPaused
        ? pauseState.pauseSource?.id || pauseState.pauseSource?.checklist_item_id
        : null;
    const isOverdue = task?.is_overdue
        || (task?.due_at && !isCompleted && !isArchived && !isPaused
            && new Date(parseApiDate(task.due_at)) < new Date());
    const dep = task?.depends_on;
    const depTitle = (() => {
        if (!dep) return null;
        if (typeof dep === 'object') return dep.title || dep.name || `Task #${dep.id}`;
        return `Task #${dep}`;
    })();
    const depStatus = dep && typeof dep === 'object' ? dep.status : null;
    const depIsBlocking = Boolean(dep && depStatus !== 'completed' && depStatus !== 'archived');
    const leadName = lead
        ? (lead.full_name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '—')
        : null;
    const treatmentName = task?.lead_treatment?.treatment_name || null;
    const activeTreatments = pickActiveTreatments(lead);

    // Lead-journey context dates for the slideout. Treatment is the task's
    // linked lead_treatment, else the most-recently-updated treatment on the
    // lead. Rows render only when their date resolves.
    const journeyTreatment = task?.lead_treatment
        || (Array.isArray(task?.lead?.lead_treatments) && task.lead.lead_treatments.length > 0
            ? [...task.lead.lead_treatments].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0]
            : null);
    const journeyLogs = journeyTreatment?.state_transition_logs || [];
    const enquiryDate = formatJourneyDate(task?.lead?.created_at);
    const consultationAttendedDate = formatJourneyDate(findTransitionDate(journeyLogs, 'to_consultation_attended'));
    const treatmentStartedDate = formatJourneyDate(findTransitionDate(journeyLogs, 'to_treatment_started'));

    // ---- handlers ----
    // ``handleRefresh`` is called by ChecklistItemRow's ``onDone`` after a
    // successful completion PATCH. At that point the BE has already flipped
    // the parent ``Task.status`` to ``completed`` if every required item
    // is YES, but our local ``task`` slice still holds the *pre-completion*
    // checklist (the new item state hasn't been re-fetched yet).
    //
    // We use that pre-fetch snapshot to detect "this completion finishes
    // the task": if exactly ONE item was still pending before this call,
    // then this call was the last one and the parent task is about to flip
    // to ``completed``. Broadcasting BEFORE the refetch lets list views
    // play the exit animation immediately; the refetch is deferred by
    // ``TASK_EXIT_MS`` so the animation actually plays before the BE
    // response confirms the removal (otherwise the row would vanish
    // instantly the moment the V2 list refetches).
    //
    // Note: this heuristic is intentionally synchronous + local. We do NOT
    // wait on the refetch + read ``task.currentTask.status === 'completed'``
    // because the spec wants the animation to start AS the checklist tick
    // resolves, not after a network round-trip.
    // P2-2 M2: track the deferred-refetch timer so unmount can clear it
    // and we don't dispatch a getTask after the slideout has closed.
    const deferredRefreshTimerRef = useRef(null);
    useEffect(() => () => {
        if (deferredRefreshTimerRef.current) {
            clearTimeout(deferredRefreshTimerRef.current);
            deferredRefreshTimerRef.current = null;
        }
    }, []);

    const handleRefresh = (wasGenuineCompletion = false) => {
        // Count items still in PENDING — SKIPPED/PAUSED don't block task
        // completion on the BE (is_completed derives from YES/SKIPPED/PAUSED),
        // so they shouldn't count as "still pending" for the exit-animation
        // heuristic either. If a user Skips the last truly-PENDING item the
        // parent task transitions to completed and we want the same Undo flow.
        const pendingItems = (checklist || []).filter(
            (it) => (it.completion_state || CHECKLIST_COMPLETION_STATE.PENDING) === CHECKLIST_COMPLETION_STATE.PENDING
        );
        const wasLastPending = pendingItems.length === 1;
        // P2-2 I4: V2-only broadcast. Gate on ``tasks-v2`` feature access
        // so practices still on V1 (or with V2 disabled) never emit the
        // exit-animation event — keeps V1 surfaces inert even if a stray
        // V2 mount lingers in the tree during a practice switch.
        const v2Enabled = hasFeatureAccess('tasks-v2', practiceId);
        if (v2Enabled && wasLastPending && task?.id && wasGenuineCompletion) {
            // Phase 3 — bundle a snapshot of the pre-completion task + the
            // id of the just-resolved checklist item so the Undo Snackbar
            // can both restore the card visually and POST /reopen/ to flip
            // the item back to PENDING. The broadcast only fires when
            // exactly ONE item was still pending, so that item IS the one
            // we need to reopen.
            const lastItem = pendingItems[0];
            const lastItemId = lastItem?.id || lastItem?.checklist_item_id || null;
            broadcastTaskCompleting(task.id, TASK_EXIT_MS, {
                taskSnapshot: task,
                lastItemId,
            });
            // Defer the slice refetch so the list-view exit animation has
            // time to play. The slideout itself is independent — its task
            // detail will refresh after the same delay.
            if (deferredRefreshTimerRef.current) {
                clearTimeout(deferredRefreshTimerRef.current);
            }
            deferredRefreshTimerRef.current = setTimeout(() => {
                deferredRefreshTimerRef.current = null;
                if (taskId && practiceId) dispatch(getTask(practiceId, taskId));
                if (onTaskUpdated) onTaskUpdated();
            }, TASK_EXIT_MS);
            return;
        }
        if (taskId && practiceId) dispatch(getTask(practiceId, taskId));
        if (onTaskUpdated) onTaskUpdated();
    };

    const handleUnpauseChecklistItem = async () => {
        if (!practiceId || !taskId || !pausedChecklistItemId) return;
        setUnpausingChecklist(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${pausedChecklistItemId}/reopen/`,
                'post'
            );
            if (res?.status === 200 || res?.status === 204) {
                dispatch(updateError({ success: true, message: 'Checklist resumed.' }));
                handleRefresh();
            } else {
                dispatch(updateError({ success: false, message: 'Failed to unpause checklist.' }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to unpause checklist.' }));
        } finally {
            setUnpausingChecklist(false);
        }
    };

    // #318 — Build the rowLead shape the follow-up popups expect, sourced from
    // task.lead + task.lead.lead_treatments[] (latest by updated_at, same as
    // the V2 journey derivation). Mirrors V1's handlePopup/leadDataHandler shape.
    const buildRowLead = () => {
        const latestTreatment = (() => {
            const treatments = task?.lead?.lead_treatments;
            if (!Array.isArray(treatments) || treatments.length === 0) return null;
            return [...treatments].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
        })();
        return {
            ...task?.lead,
            lead_id: task?.lead?.id || '',
            treatment_id: latestTreatment?.id || '',
            price: latestTreatment?.price || '',
            first_name: task?.lead?.first_name || '',
            last_name: task?.lead?.last_name || '',
            status: latestTreatment?.status || '',
            treatment_name: latestTreatment?.treatment || '',
            created_at_human: task?.lead?.created_at_human,
            lead_treatments__lead_contacted_counts__count:
                latestTreatment?.lead_contacted_counts?.find(
                    (item) => item.status === latestTreatment?.status
                )?.count || 0,
            clinCheckData: latestTreatment?.leadtreatment_clincheck,
            leadTreatmentSpecification: latestTreatment?.leadtreatment_specification,
            treatmentData: latestTreatment,
            taskId: task?.id,
        };
    };

    // #318 — Status-routed Follow Up handler. Replaces navigate-to-lead-detail.
    // Mirrors V1 TaskReminder.handleFollowUp exactly.
    const handleActionZoneSuccess = (action) => {
        // 'BOOK_VIA_WIZARD' — emitted by LeadActionZone (bookRoutesToWizard) when
        // the user picks "Book Consultation" from the task-detail CTA. Route it
        // through the SAME status-aware Follow Up flow the leads table uses: for a
        // NEW lead this opens ContactLeadPopupRouter → FollowUpV2 (matching the
        // table's "Book appointment", which also opens FollowUpV2 on its default
        // tab — the user then clicks the Book chip). We reuse the FOLLOW_UP
        // routing below rather than duplicating the popup-open logic.
        const effectiveAction = action === 'BOOK_VIA_WIZARD' ? 'FOLLOW_UP' : action;
        if (effectiveAction !== 'FOLLOW_UP') {
            handleRefresh(true);
            return;
        }
        // Status resolution order (most specific first):
        //   1. `task.lead_treatment` — direct FK from the task to its anchor
        //      treatment. If the task was created against a specific treatment
        //      (the normal case for tasks-v2), this is authoritative.
        //   2. Latest treatment from `task.lead.lead_treatments[]` by
        //      updated_at — fallback when the FK isn't hydrated.
        //   3. `task.lead.status` — lead-level status, used when the lead has
        //      no treatments at all (e.g. archived lead in NEW status).
        //   4. 'NEW' — last-resort default.
        // Previously this only checked (2) and defaulted to NEW, which
        // misrouted BOOKED leads to OpenContactLeadPopup when the embedded
        // lead_treatments[] array wasn't populated on the task payload.
        const linkedTreatment = task?.lead_treatment;
        const latestTreatment = (() => {
            if (linkedTreatment?.status) return linkedTreatment;
            const treatments = task?.lead?.lead_treatments;
            if (!Array.isArray(treatments) || treatments.length === 0) return null;
            return [...treatments].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
        })();
        const effectiveStatus = latestTreatment?.status || task?.lead?.status || 'NEW';
        const leadId = task?.lead?.id;
        if (!leadId || !practiceId) { handleRefresh(); return; }

        localStorageRemoveItem('localTreatmentData');
        const rl = buildRowLead();
        setRowLead(rl);

        // #318 review-fix-6 — match V1's body lock on popup open.
        document.querySelector('body')?.classList.add('overflow-hidden');

        // Route via the shared resolver so this task-detail CTA opens the same
        // logical popup as every other fire point for a given status
        // (popup-routing-consolidation Phase 3). The resolver owns status
        // classification + aliases; this call site keeps its side-effects and the
        // concrete Attended-popup variant split, a Phase 4 concern.
        const normalized = normalizeStatus(effectiveStatus);
        // A BOOKED lead's Follow Up means "record attendance" (lead has an
        // upcoming appointment), so declare RECORD_ATTENDED intent to preserve
        // today's behaviour — mirrors the OverView booked CTA.
        const intent = normalized === 'CONSULTATION_BOOKED' ? ACTION.RECORD_ATTENDED : undefined;
        const lt = linkedTreatment || latestTreatment;
        const { popup } = resolveLeadActionPopup({
            status: effectiveStatus,
            action: intent,
            practiceId,
            leadId,
            leadTreatmentId: lt?.id,
            source: 'task-detail:LeadActionZone',
        });

        // TREATMENT_STARTED — open the treatment-in-progress hub instead of the
        // consultation-attended popup. The hub expects the same row shape as the
        // TreatmentStarted table (treatment_name, price, treatmentData).
        if (popup === POPUP.TREATMENT_STARTED) {
            setHubRow({
                lead_id: leadId,
                id: leadId,
                first_name: task?.lead?.first_name || '',
                last_name: task?.lead?.last_name || '',
                status: 'TREATMENT_STARTED',
                treatment_id: lt?.id,
                treatment_name: lt?.treatment || lt?.treatment_name || '',
                price: lt?.price || lt?.custom_price || '',
                // Single-element so the hub's resolveRowTreatment reads THIS
                // treatment at [0] — not an arbitrary one from a multi-treatment
                // lead (which would resolve the wrong checklist).
                treatmentData: lt ? [lt] : [],
            });
            setOpenTreatmentHub(true);
            return;
        }

        // The Attended popup serves three statuses here: CONSULTATION_ATTENDED and
        // CONSULTATION_BOOKED (resolver → ATTENDED), plus CLOSED — a closed lead's
        // task Follow Up views the consultation via the same popup, which the
        // canonical resolver maps to READ_ONLY, so handle CLOSED explicitly.
        if (popup === POPUP.ATTENDED || normalized === 'CLOSED') {
            // #318 review-fix-4 — 800ms (matches V1) so followUpSlice +
            // getAppointment resolve before the popup queries redux.
            // BOOKED primes the popup with the 'Attended' variant (Record Attended
            // / FTA / reschedule); ATTENDED & CLOSED view it via 'consultation'.
            dispatch(updateFollowUpLead(normalized === 'CONSULTATION_BOOKED' ? 'Attended' : 'consultation'));
            dispatch(setCurrentTreatmentDataGlobal({
                lead_id: leadId,
                first_name: task?.lead?.first_name || '',
                last_name: task?.lead?.last_name || '',
                treatmentData: latestTreatment,
            }));
            dispatch(followUpSlice(leadId, practiceId));
            setTimeout(() => { dispatch(getAppointment(practiceId, leadId)); setOpenAttended(true); }, 800);
            return;
        }

        if (popup === POPUP.FTA) {
            // #318 review-fix-5 — clear stale treatment-global so FTA popup
            // doesn't inherit a previous follow-up's data.
            dispatch(setCurrentTreatmentDataGlobal(null));
            dispatch(updateFollowUpLead('UnAttended'));
            dispatch(followUpSlice(leadId, practiceId));
            setTimeout(() => { dispatch(getAppointment(practiceId, leadId)); setOpenUnAttended(true); }, 800);
            return;
        }

        // FOLLOW_UP (NEW + any unmapped status) → contact dialog.
        dispatch(initializeFacebookAuthToken());
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(updateFollowUpLead('contact'));
        dispatch(getActivities(practiceId, leadId, 10, 0));
        dispatch(followUpSlice(leadId, practiceId));
        // Distinguish the originating CTA: the "Book Consultation" chip emits
        // BOOK_VIA_WIZARD (routed through FOLLOW_UP above), vs the plain Follow Up CTA.
        const ctaLabel = action === 'BOOK_VIA_WIZARD' ? 'Book(wizard)' : 'FollowUp';
        setContactLeadDebugSource(`task-detail:LeadActionZone→${ctaLabel}(status=${effectiveStatus})`);
        setTimeout(() => setOpenContactLead(true), 800);
    };

    // Tear down the hub: clear local state, release the body scroll-lock the
    // FOLLOW_UP handler set, and refresh so the now-CLOSED task drops off the
    // list. Mirrors handleFollowUpPopupClose's cleanup.
    // wasGenuineCompletion=true when called after a successful CompleteTreatment
    // POST so the task-exit animation fires; false (default) for cancel/close.
    const closeTreatmentHub = (wasGenuineCompletion = false) => {
        setOpenTreatmentHub(false);
        setHubRow(null);
        // CompleteTreatment's success path leaves the global loader on; clear it
        // here so it doesn't leak after the hub unmounts.
        dispatch(setLoader(false));
        setTimeout(() => { document.querySelector('body')?.classList.remove('overflow-hidden'); }, 200);
        handleRefresh(wasGenuineCompletion);
    };

    const handleTreatmentHubYes = () => {
        // setLoader(true) drives the hub's Complete spinner/disabled state,
        // guarding the irreversible treatment_closed POST against double-submit.
        dispatch(setLoader(true));
        dispatch(CompleteTreatment(
            { treatment: hubRow?.treatment_name || '', custom_price: hubRow?.price },
            hubRow?.lead_id,
            hubRow?.treatment_id,
            practiceId,
            () => { closeTreatmentHub(true); },
        ));
    };

    const handleFollowUpPopupClose = () => {
        setOpenContactLead(false);
        setOpenAttended(false);
        setOpenUnAttended(false);
        setValidStep(false);
        setRowLead(null);
        // #318 review-fix — clear global lead-row state so the next slideout
        // session doesn't inherit it (caused "auto-open"-feeling popups).
        dispatch(tableLead(null));
        dispatch(updateSteps(null));
        dispatch(updateFollowUpLead(null));
        dispatch(setCurrentTreatmentDataGlobal(null));
        setTimeout(() => { document.querySelector('body')?.classList.remove('overflow-hidden'); }, 200);
        handleRefresh();
    };

    const [appointmentOutcomeSelection, setAppointmentOutcomeSelection] = useState(null);
    const handleNoAttendedFromAttendedPopup = (selection) => {
        setAppointmentOutcomeSelection(selection);
        dispatch(updateFollowUpLead('UnAttended'));
        if (task?.lead?.id && practiceId) {
            dispatch(followUpSlice(task.lead.id, practiceId));
        }
        setOpenAttended(false);
        setOpenUnAttended(true);
    };

    const handleSaveNotes = () => {
        setSavingNotes(true);
        dispatch(
            updateTask(practiceId, taskId, { description: notes }, ({ success }) => {
                if (success) setNotesDirty(false);
                setSavingNotes(false);
            })
        );
    };

    const handleSaveAssignees = async (override) => {
        if (!Array.isArray(override)) return;
        const list = override;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/`,
                'patch',
                { assignees: list.map((pu) => ({ practice_user_id: pu.id })) }
            );
            if (response.status === 200) {
                // Optimistic in-place update: push the API-returned task
                // directly into Redux so the assignee chips refresh instantly.
                // Intentionally NOT calling getTask (which calls
                // startCurrentTaskLoading → blanks the slideout) or
                // onTaskUpdated (which bumps reloadKey → full list reload).
                // The list assignee avatar is cosmetic and catches up on the
                // next natural refresh.
                dispatch(setCurrentTask(response.data));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to update assignees.' }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to update assignees.' }));
        }
    };

    const handlePatchField = async (field, value) => {
        setSavingField(field);
        setFieldError(null);
        if (task) dispatch(setCurrentTask({ ...task, [field]: value }));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/`,
                'patch',
                { [field]: value }
            );
            if (response.status === 200) {
                dispatch(setCurrentTask(response.data));
                dispatch(updateError({ success: true, message: field === 'title' ? 'Task name updated' : 'Task updated successfully' }));
                dispatch(getTask(practiceId, taskId));
                if (onTaskUpdated) onTaskUpdated();
            } else {
                const msg = response.data?.detail || response.data?.[field]?.[0] || `Failed to update ${field}.`;
                setFieldError(msg);
                dispatch(getTask(practiceId, taskId));
            }
        } catch {
            setFieldError(`Failed to update ${field}.`);
            dispatch(getTask(practiceId, taskId));
        }
        setSavingField(null);
    };

    // Inline-rename handlers — declared after handlePatchField to avoid TDZ.
    const handleCancelRename = () => {
        setRenamingTitle(false);
        setRenameValue('');
    };

    const handleCommitRename = () => {
        const trimmed = renameValue.trim();
        if (!trimmed || trimmed === (task?.title || task?.name || '')) {
            handleCancelRename();
            return;
        }
        setRenamingTitle(false);
        handlePatchField('title', trimmed);
    };

    const handleStartRename = (e) => {
        if (isPaused) return;
        e.stopPropagation();
        setRenameValue(task?.title || task?.name || '');
        setRenamingTitle(true);
    };

    const handleRenameKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleCommitRename(); }
        if (e.key === 'Escape') { handleCancelRename(); }
    };

    const patchTreatmentBinding = async (treatmentId, treatmentLabel) => {
        if (!practiceId || !taskId || !treatmentId) return;
        // Sticky guard: even on a silent BE no-op (200 without persistence),
        // do not retry the same (task, treatment) pair this mount. Prevents
        // the PATCH→getTask→re-render→PATCH loop that occurred when BE
        // accepted the PATCH but didn't actually persist (e.g. NEW-status
        // treatments rejected by a downstream validator without surfacing
        // a 400). See QA report #244.
        autoBindAttemptedRef.current = true;
        try {
            const res = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', {
                lead_treatment: treatmentId,
            });
            const echoed = res?.data?.lead_treatment;
            const persisted = res?.status === 200 && echoed
                && (echoed.id ?? echoed) === treatmentId;
            if (persisted) {
                if (treatmentLabel) setAutoBindLabel(treatmentLabel);
                if (taskId && practiceId) dispatch(getTask(practiceId, taskId));
                if (onTaskUpdated) onTaskUpdated();
            } else if (res?.status !== 200) {
                dispatch(updateError({ success: false, message: 'Failed to link treatment.' }));
            }
            // 200-no-persist: stay silent; ref already set above so we don't
            // hammer the endpoint.
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to link treatment.' }));
        }
    };

    // Auto-bind treatment when task has none and lead has exactly one active
    useEffect(() => {
        if (!task || !taskId || !practiceId) return;
        if (task?.lead_treatment) return;
        if (autoBindAttemptedRef.current) return;
        if (activeTreatments.length !== 1) return;
        autoBindAttemptedRef.current = true;
        const t = activeTreatments[0];
        const label = t?.treatment_name || t?.treatment?.name || `Treatment #${t?.id}`;
        patchTreatmentBinding(t.id, label);
    }, [task?.id, task?.lead_treatment, activeTreatments.length, taskId, practiceId]);

    const handleDepSearch = async (query) => {
        if (!query || query.length < 2) { setDepOptions([]); return; }
        setDepSearchLoading(true);
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/?search=${encodeURIComponent(query)}&limit=20&status__in=pending,in_progress`
            );
            if (response.status === 200) {
                const results = (response.data?.results || []).filter((t) => String(t.id) !== String(taskId));
                setDepOptions(results);
            }
        } catch { /* silent */ }
        finally { setDepSearchLoading(false); }
    };

    const handleSetDep = async (selectedTask) => {
        if (!selectedTask) return;
        setDepPickerOpen(false);
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', {
                depends_on: selectedTask.id,
            });
            if (response.status === 200) handleRefresh();
            else dispatch(updateError({ success: false, message: 'Failed to set dependency.' }));
        } catch { dispatch(updateError({ success: false, message: 'Failed to set dependency.' })); }
    };

    const handleRemoveDep = async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', { depends_on: null });
            if (response.status === 200) handleRefresh();
            else dispatch(updateError({ success: false, message: 'Failed to remove dependency.' }));
        } catch { dispatch(updateError({ success: false, message: 'Failed to remove dependency.' })); }
    };

    const handleLegacyActionComplete = async (action) => {
        if (!task || !practiceId || !taskId) return;
        const ACTION_TO_CELL_TYPES = {
            CLOSE:              [CHECKLIST_ITEM_TYPE.PHONE_CALLED, CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL],
            BOOK:               [CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT],
            BOOK_CLOSE:         [CHECKLIST_ITEM_TYPE.BOOK_APPOINTMENT],
            ATTENDED:           [CHECKLIST_ITEM_TYPE.RECORD_ATTENDANCE],
            FAILED_TO_ATTEND:   [CHECKLIST_ITEM_TYPE.RECORD_FAILED_TO_ATTEND],
            START_TREATMENT:    [CHECKLIST_ITEM_TYPE.START_TREATMENT],
            COMPLETE:           [CHECKLIST_ITEM_TYPE.COMPLETE_TREATMENT],
        };
        const targetTypes = ACTION_TO_CELL_TYPES[action];
        if (!targetTypes) return;
        const pendingCell = checklist.find(
            (it) => targetTypes.includes(it.item_type)
                && (it.completion_state || CHECKLIST_COMPLETION_STATE.PENDING) === CHECKLIST_COMPLETION_STATE.PENDING
        );
        if (!pendingCell) return;
        const itemId = pendingCell.id || pendingCell.checklist_item_id;
        if (!itemId) return;
        try {
            await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'post',
                { completion_state: 'YES', response_payload: { source: 'legacy_action_zone', action } }
            );
            handleRefresh(true);
        } catch { /* silent */ }
    };

    if (!task) return null;

    // Detail mode tweaks: Properties label width is wider for the full-page canvas.
    const propsLabelWidth = mode === 'detail' ? 140 : 110;

    return (
        <>
            {/* ---- V1-style status banner (top) ----
                Sidebar/modal mode: the parent (TaskSlideout) renders the
                banner above this body so it stays sticky outside the
                scrollable area. Detail mode: render inline so the page
                gets the same polish. */}
            {mode !== 'sidebar' && mode !== 'modal' && (() => {
                const palette =
                    isCompleted ? { bg: alpha(primary.main, 0.08), border: primary.main, fg: primary.dark, icon: 'mdi:check-circle' } :
                    isArchived  ? { bg: alpha(grey[500], 0.10), border: grey[600],  fg: grey[800],     icon: 'mdi:archive-outline' } :
                    isPaused    ? { bg: alpha(warning.main, 0.10), border: warning.main, fg: 'warning.dark', icon: 'eva:pause-circle-fill' } :
                    isOverdue   ? { bg: '#fef2f2', border: '#dc2626', fg: '#dc2626', icon: 'mdi:alert' } :
                                  { bg: alpha(primary.main, 0.06), border: primary.main, fg: primary.dark, icon: 'eva:radio-button-on-fill' };
                let pausedLabel = 'Paused';
                if (isPaused && task?.resume_at) {
                    const resumeDayjs = parseApiDate(task.resume_at);
                    if (resumeDayjs && resumeDayjs.isValid && resumeDayjs.isValid()) {
                        const resumeMs = resumeDayjs.valueOf();
                        const dateStr = resumeDayjs.toDate().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
                        const tick = nowTick || Date.now();
                        const diffMin = Math.round((resumeMs - tick) / 60_000);
                        let suffix = '';
                        if (diffMin > 0) {
                            const days = Math.floor(diffMin / 1440);
                            const hours = Math.floor((diffMin % 1440) / 60);
                            const mins = diffMin % 60;
                            const parts = [];
                            if (days) parts.push(`${days}d`);
                            if (hours) parts.push(`${hours}h`);
                            if (mins && !days) parts.push(`${mins}m`);
                            suffix = parts.length ? ` (in ${parts.join(' ')})` : '';
                        } else if (diffMin <= 0 && diffMin > -2) {
                            suffix = ' (any moment now)';
                        }
                        pausedLabel = `Paused · resumes ${dateStr}${suffix}`;
                    }
                }
                const label =
                    isCompleted ? 'Completed' :
                    isArchived  ? 'Archived' :
                    isPaused    ? pausedLabel :
                    isOverdue   ? 'Overdue' :
                                  'Active';
                return (
                    <Box className="task-slideout-status-banner" data-testid="task-slideout-status-banner" sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 1, px: 2, py: 0.75,
                        bgcolor: palette.bg,
                        borderLeft: `3px solid ${palette.border}`,
                        borderBottom: '1px solid', borderColor: 'divider',
                        mb: mode === 'detail' ? 2 : 0,
                        borderRadius: mode === 'detail' ? 1 : 0,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Iconify icon={palette.icon} width={14} sx={{ color: palette.fg, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: palette.fg, textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }} noWrap>
                                {label}
                            </Typography>
                        </Box>
                    </Box>
                );
            })()}

            {/* ---- Content area ---- #316 px tightened 2.5 → 1.5 in slideout/modal modes */}
            <Box sx={{ px: mode === 'detail' ? 0 : 1.5, pt: mode === 'detail' ? 0 : 1 }}>
                {isPaused && pausedChecklistItemId && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1.5,
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                            bgcolor: alpha(warning.main, 0.08),
                            border: '1px solid',
                            borderColor: alpha(warning.main, 0.25),
                        }}
                    >
                        <Typography sx={{ fontSize: '12px', color: 'warning.dark', fontWeight: 600 }}>
                            This checklist is paused. Unpause it before editing or completing.
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={unpausingChecklist}
                            onClick={handleUnpauseChecklistItem}
                            startIcon={unpausingChecklist
                                ? <MuiCircularProgress size={14} sx={{ color: 'inherit' }} />
                                : <Iconify icon="eva:play-circle-outline" width={14} />
                            }
                            sx={{ textTransform: 'none', flexShrink: 0 }}
                        >
                            {unpausingChecklist ? 'Unpausing…' : 'Unpause checklist'}
                        </Button>
                    </Box>
                )}

                {/* Task title — ad-hoc tasks show an inline rename pencil; template tasks are read-only */}
                <Box
                    className="task-slideout-title-row"
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1,
                        '& .rename-btn': { opacity: 0, transition: 'opacity 0.15s' },
                        '&:hover .rename-btn': { opacity: 1 },
                    }}
                >
                    {renamingTitle ? (
                        <TextField
                            inputRef={renameTitleRef}
                            fullWidth
                            size="small"
                            autoFocus
                            disabled={savingField === 'title' || isPaused}
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={handleRenameKeyDown}
                            onBlur={handleCommitRename}
                            inputProps={{ 'data-testid': 'task-rename-input', 'aria-label': 'Rename task', maxLength: 500 }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: mode === 'detail' ? '1.1rem' : '0.9rem',
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    p: '2px 6px',
                                },
                                '& .MuiOutlinedInput-root': { borderRadius: '6px' },
                            }}
                        />
                    ) : (
                        <>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography className="task-slideout-title" data-testid="task-slideout-title" sx={{
                                    fontSize: mode === 'detail' ? '1.1rem' : '0.9rem',
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    textDecoration: isCompleted ? 'line-through' : 'none',
                                    opacity: isCompleted ? 0.5 : 1,
                                    transition: 'opacity 0.2s ease',
                                }}>
                                    {interpolateTaskMergeTags(task?.title || task?.name || 'Untitled Task', task)}
                                </Typography>
                                {savingField === 'title' && (
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.25 }}>Saving…</Typography>
                                )}
                            </Box>
                            {isAdHocTask && !isCompleted && !isArchived && (
                                <Tooltip title={isPaused ? 'Unpause this checklist before editing' : 'Rename task'}>
                                    <span>
                                        <IconButton
                                            className="rename-btn"
                                            size="small"
                                            onClick={handleStartRename}
                                            disabled={savingField === 'title' || isPaused}
                                            data-testid="task-rename-btn"
                                            sx={{ p: 0.25, color: 'text.disabled', flexShrink: 0, mt: '2px', '&:hover': { color: 'primary.main' } }}
                                        >
                                            <Iconify icon="eva:edit-2-outline" width={14} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}
                </Box>

                {/* Lead block (avatar + name + journey status chip)
                    #316 — chip on the right surfaces the lead's current journey
                    stage so the slideout matches the V1 parity Raj asked for.
                    Sources the status from the most-recently-updated entry in
                    lead.lead_treatments (same path the V2 cards use). */}
                {leadName && (() => {
                    const journeyStatus = (() => {
                        if (leadTreatment?.status) return leadTreatment.status;
                        const treatments = lead?.lead_treatments;
                        if (!Array.isArray(treatments) || treatments.length === 0) return null;
                        const sorted = [...treatments].sort((a, b) =>
                            new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
                        );
                        return sorted[0]?.status || null;
                    })();
                    return (
                        <Box className="task-slideout-lead-name" data-testid="task-slideout-lead-name" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 1 }}>
                            <Avatar {...piiProps} sx={{ width: 32, height: 32, fontSize: '0.72rem', bgcolor: alpha(primary.main, 0.16), color: primary.main, flexShrink: 0 }}>
                                {leadName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography {...piiProps} sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3, minWidth: 0, flex: 1 }} noWrap>
                                {leadName}
                            </Typography>
                            {journeyStatus && (
                                <Chip
                                    size="small"
                                    label={getJourneyLabel(journeyStatus)}
                                    sx={{
                                        height: 22,
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        bgcolor: alpha(getJourneyDot(journeyStatus), 0.12),
                                        color: getJourneyDot(journeyStatus),
                                        borderRadius: '6px',
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                        </Box>
                    );
                })()}

                {/* Contact-count meta */}
                {lead?.id && (() => {
                    const currentStatus = leadTreatment?.status || lead?.lead_treatments?.[0]?.status;
                    const counts = lead?.lead_contacted_counts || [];
                    const entry = counts.find((c) => c.status === currentStatus);
                    const count = entry?.count || 0;
                    if (count === 0) return null;
                    const lastContactedAt = entry?.last_contacted_at;
                    const lastContactedLabel = lastContactedAt
                        ? (() => {
                            const diff = Math.floor((Date.now() - new Date(lastContactedAt).getTime()) / 86400000);
                            if (diff === 0) return 'today';
                            if (diff === 1) return 'yesterday';
                            return `${diff} days ago`;
                        })()
                        : null;
                    return (
                        <Typography
                            sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}
                            title="Contact count for current lead status"
                        >
                            <Iconify icon="eva:phone-outline" width={13} sx={{ flexShrink: 0, color: 'text.disabled' }} />
                            {`Contacted ${count} time${count !== 1 ? 's' : ''} in this status${lastContactedLabel ? ` · Last: ${lastContactedLabel}` : ''}`}
                        </Typography>
                    );
                })()}

                <Divider sx={{ mb: 1.5, borderColor: 'divider' }} />

                {/* LeadActionZone — keep Follow Up / Archive visible for active lead tasks.
                    Primary checklist actions render below this block; they should not remove
                    the lead-level follow-up affordance from the sidebar. */}
                {(() => {
                    const cat = (task?.category || '').toUpperCase();
                    const hideForCategory = cat === 'MARKETING' || cat === 'ADMIN' || cat === 'REVIEW';
                    const showLeadActionZone = lead?.id && !isCompleted && !isArchived && !isPaused && !hideForCategory;
                    return showLeadActionZone ? (
                        <Box className="task-slideout-action-button" data-testid="task-slideout-action-button">
                            <LeadActionZone
                                lead={lead}
                                leadTreatment={leadTreatment}
                                practiceId={practiceId}
                                onSuccess={handleActionZoneSuccess}
                                onActionComplete={handleLegacyActionComplete}
                                // Task-detail context: route the BOOK action to the
                                // same FollowUpV2 booking wizard the leads table uses
                                // (handled as 'BOOK_VIA_WIZARD' in
                                // handleActionZoneSuccess) instead of LeadActionZone's
                                // inline quick-book form.
                                bookRoutesToWizard
                            />
                        </Box>
                    ) : null;
                })()}

                {/* Ghost-task affordance: 0-item checklist gets a direct
                    "Mark task complete" button so users can close ghost
                    tasks without navigating to a separate surface. */}
                {checklist.length === 0 && !isPaused && (
                    <Box sx={{ mb: 1.5 }}>
                        <TaskDetailChecklist
                            items={[]}
                            practiceId={String(practiceId)}
                            taskId={String(taskId)}
                            leadData={lead}
                            isCompleted={isCompleted}
                            onComplete={() => handleRefresh(true)}
                        />
                    </Box>
                )}

                {/* Field edit error toast */}
                {fieldError && (
                    <Box sx={{ mb: 1, px: 1.5, py: 0.75, borderRadius: '8px', bgcolor: alpha(error.main, 0.08), border: '1px solid', borderColor: alpha(error.main, 0.2) }}>
                        <Typography sx={{ fontSize: '12px', color: 'error.main' }}>{fieldError}</Typography>
                    </Box>
                )}

                {/* ---- Properties grid ---- */}
                <Box className="task-slideout-details" data-testid="task-slideout-details" sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5, display: 'block' }}>
                        Details
                    </Typography>
                    <Box>
                        {/* Treatment row */}
                        {lead?.id && (() => {
                            const displayName = treatmentName || autoBindLabel;
                            const canPick = !isPaused && (activeTreatments.length > 1 || (!displayName && activeTreatments.length > 0));
                            return (
                                <Box
                                    className="task-slideout-detail-row" data-testid="task-slideout-detail-row-treatment"
                                    sx={{
                                        display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75,
                                        borderBottom: '1px solid', borderColor: 'divider',
                                        cursor: canPick ? 'pointer' : 'default',
                                        '&:hover': canPick ? { bgcolor: 'action.hover', borderRadius: '8px' } : {},
                                    }}
                                    onClick={canPick ? (e) => setTreatmentRowAnchor(e.currentTarget) : undefined}
                                >
                                    <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                        <Iconify icon="mdi:tooth-outline" width={15} />
                                    </Box>
                                    <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>Treatment</Typography>
                                    <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                        {displayName ? (
                                            <Typography sx={{ fontSize: '13px', color: 'text.primary', fontWeight: 500, textAlign: 'right' }} noWrap>
                                                {displayName}
                                            </Typography>
                                        ) : (
                                            <Typography sx={{ fontSize: '13px', color: canPick ? primary.main : 'text.disabled', fontWeight: 500, textAlign: 'right' }}>
                                                {canPick ? 'Link a treatment' : 'No active treatment'}
                                            </Typography>
                                        )}
                                    </Box>
                                    {canPick && (
                                        <Iconify icon="eva:edit-2-outline" width={13} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                                    )}
                                </Box>
                            );
                        })()}
                        <Popover
                            open={Boolean(treatmentRowAnchor)}
                            anchorEl={treatmentRowAnchor}
                            onClose={() => setTreatmentRowAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            PaperProps={{ sx: { p: 1.5, width: 280, borderRadius: 2 } }}
                        >
                            <Autocomplete
                                size="small"
                                options={activeTreatments}
                                getOptionLabel={(t) => t?.treatment_name || t?.treatment?.name || `Treatment #${t?.id}` || ''}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Select a treatment…" autoFocus />
                                )}
                                onChange={(_e, value) => {
                                    if (!value) return;
                                    const label = value.treatment_name || value.treatment?.name || `Treatment #${value.id}`;
                                    setTreatmentRowAnchor(null);
                                    patchTreatmentBinding(value.id, label);
                                }}
                            />
                        </Popover>

                        {/* Assigned to */}
                        <Box className="task-slideout-detail-row" data-testid="task-slideout-detail-row-assignees" sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                <Iconify icon="eva:people-outline" width={15} />
                            </Box>
                            <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>Assigned to</Typography>
                            <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                <AssignedToLead
                                    assignToState={assignees}
                                    setAssignTo={(next) => {
                                        handleSaveAssignees(next);
                                    }}
                                    staffRole="assignee"
                                    context="task"
                                />
                            </Box>
                        </Box>

                        {/* Assignment-provenance row (ISS-006) — who assigned each
                            assignee, and when. Mirrors the Created-by row's icon
                            column / label / avatar+name+date layout and reuses
                            ``formatDue`` so the date format matches. Hidden entirely
                            when the task has no assignments. */}
                        {assignmentProvenance.length > 0 && (
                            <Box className="task-slideout-detail-row" data-testid="task-slideout-detail-row-assigned-by" sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled', mt: 0.5 }}>
                                    <Iconify icon="eva:person-done-outline" width={15} />
                                </Box>
                                <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0, mt: 0.5 }}>Assigned by</Typography>
                                <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
                                    {assignmentProvenance.map((p) => {
                                        // Only qualify by assignee when there is more than
                                        // one — the single-assignee case reads cleanly as
                                        // "Assigned by <name> · <date>".
                                        const showAssignee = assignmentProvenance.length > 1 && !!p.assigneeName;
                                        const when = formatDue(p.assignedAt);
                                        const whenNode = when ? (
                                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', ml: 'auto', flexShrink: 0 }}>
                                                {when}
                                            </Typography>
                                        ) : null;
                                        const assigneePrefix = showAssignee ? (
                                            <Typography sx={{ fontSize: '13px', color: 'text.disabled', flexShrink: 0 }}>
                                                {p.assigneeName} ·
                                            </Typography>
                                        ) : null;
                                        if (!p.by) {
                                            return (
                                                <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                    {assigneePrefix}
                                                    <Tooltip title={`${p.assigneeName || 'This assignee'} was assigned by an automated process, not by a colleague`}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, overflow: 'hidden' }}>
                                                            <Iconify icon="eva:settings-2-outline" width={15} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                                                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                Assigned automatically
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                    {whenNode}
                                                </Box>
                                            );
                                        }
                                        const byInitials = ((p.by.first_name?.[0] || '') + (p.by.last_name?.[0] || '')).toUpperCase() || '?';
                                        // Same avatar-resolution path as the Created-by row:
                                        // match the already-loaded practice-user list on user.id.
                                        const byMatch = practiceUserResults.find((pu) => pu?.user?.id === p.by.id);
                                        const byAvatarSrc = byMatch?.user?.profile_picture || undefined;
                                        return (
                                            <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                {assigneePrefix}
                                                <Tooltip title={`${p.assigneeName ? `${p.assigneeName} assigned by ` : 'Assigned by '}${p.byName}${when ? ` · ${when}` : ''}`}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, overflow: 'hidden' }}>
                                                        <Avatar src={byAvatarSrc} sx={{ width: 18, height: 18, fontSize: '0.6rem', flexShrink: 0 }}>
                                                            {byInitials}
                                                        </Avatar>
                                                        <Typography sx={{ fontSize: '13px', color: 'text.primary', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {p.byName}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                                {whenNode}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {/* Due date row */}
                        {task?.due_at && (
                            <Box
                                className="task-slideout-detail-row" data-testid="task-slideout-detail-row-due-date"
                                sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', cursor: isCompleted || isArchived || isPaused ? 'default' : 'pointer', '&:hover': isCompleted || isArchived || isPaused ? {} : { bgcolor: 'action.hover', borderRadius: '8px' } }}
                                onClick={isCompleted || isArchived || isPaused ? undefined : (e) => { setDueDateAnchor(e.currentTarget); setDueDatePicking(true); }}
                            >
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Iconify icon="eva:calendar-outline" width={15} />
                                </Box>
                                <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>Due date</Typography>
                                <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography sx={{ fontSize: '13px', color: isOverdue ? 'error.main' : 'text.primary', fontWeight: isOverdue ? 600 : 500, textAlign: 'right' }}>
                                        {savingField === 'due_at' ? 'Saving…' : formatDue(task.due_at)}
                                    </Typography>
                                </Box>
                                {!isCompleted && !isArchived && !isPaused && (
                                    <Iconify icon="eva:edit-2-outline" width={13} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                                )}
                            </Box>
                        )}

                        {/* Lead-journey context dates (read-only). Each renders
                            only when its date resolves. */}
                        {[
                            { key: 'enquiry', icon: 'eva:person-add-outline', label: 'Enquiry date', value: enquiryDate },
                            { key: 'consultation-attended', icon: 'eva:checkmark-circle-2-outline', label: 'Consultation attended', value: consultationAttendedDate },
                            { key: 'treatment-started', icon: 'eva:activity-outline', label: 'Treatment started', value: treatmentStartedDate },
                        ].filter((r) => r.value).map((r) => (
                            <Box
                                key={r.key}
                                className="task-slideout-detail-row"
                                data-testid={`task-slideout-detail-row-${r.key}`}
                                sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                            >
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Iconify icon={r.icon} width={15} />
                                </Box>
                                <Typography className="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>{r.label}</Typography>
                                <Box className="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography sx={{ fontSize: '13px', color: 'text.primary', fontWeight: 500, textAlign: 'right' }}>
                                        {r.value}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        {/* Priority row */}
                        <Box className="task-slideout-detail-row" data-testid="task-slideout-detail-row-priority" sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                <Iconify icon="eva:flag-outline" width={15} />
                            </Box>
                            <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>Priority</Typography>
                            <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                {isCompleted || isArchived || isPaused ? (
                                    <Typography sx={{ fontSize: '13px', color: PRIORITY_COLORS[priority] || 'text.primary', fontWeight: 600, textAlign: 'right' }}>
                                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                    </Typography>
                                ) : (
                                    <Select
                                        value={priority}
                                        onChange={(e) => handlePatchField('priority', e.target.value)}
                                        disabled={savingField === 'priority'}
                                        size="small"
                                        variant="standard"
                                        disableUnderline
                                        renderValue={(val) => (
                                            <Typography sx={{ fontSize: '13px', color: PRIORITY_COLORS[val] || 'text.primary', fontWeight: 600 }}>
                                                {val.charAt(0).toUpperCase() + val.slice(1)}
                                            </Typography>
                                        )}
                                        sx={{
                                            '& .MuiSelect-select': { p: 0, pr: '20px !important', textAlign: 'right' },
                                            '& .MuiSelect-icon': { fontSize: '16px', color: 'text.disabled', right: 0 },
                                            border: 'none', bgcolor: 'transparent', fontSize: '13px',
                                        }}
                                    >
                                        {Object.entries(TASK_PRIORITY).map(([, val]) => (
                                            <MenuItem key={val} value={val} sx={{ fontSize: '13px' }}>{val.charAt(0).toUpperCase() + val.slice(1)}</MenuItem>
                                        ))}
                                    </Select>
                                )}
                            </Box>
                        </Box>

                        {/* Due date picker popover */}
                        <Popover
                            open={dueDatePicking}
                            anchorEl={dueDateAnchor}
                            onClose={() => { setDueDatePicking(false); setDueDateAnchor(null); }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ sx: { p: 2, borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' } }}
                        >
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                {/* Use onAccept (fires only when the user clicks
                                    OK) instead of onChange — the previous
                                    onChange handler closed the popover and
                                    saved on the FIRST date click, before the
                                    user reached the time selector, so the
                                    time defaulted to 00:00. The action bar
                                    surfaces an explicit Cancel/OK so the
                                    flow is unambiguous. */}
                                <DateTimePicker
                                    label="Due date & time"
                                    // BE serialises dates as DD-MM-YYYY HH:mm:ss.SSSSSSZ
                                    // (non-ISO), which moment(str) parses as Invalid.
                                    // parseApiDate handles the V1/V2 format and always
                                    // returns a Day.js object or null — convert to a
                                    // native Date then moment so the picker opens
                                    // pre-populated with the task's current due_at.
                                    //
                                    // UTC/BST round-trip: newVal.toISOString() below
                                    // emits UTC; the BE is TZ-aware (Django USE_TZ=True)
                                    // and serialises read-back as London-local. Verified
                                    // 2026-05-27 with a direct PATCH:
                                    //   sent  2026-06-15T14:37:00Z
                                    //   back  15-06-2026 15:37:00 (BST = UTC+1)
                                    value={(() => {
                                        const parsed = parseApiDate(task?.due_at);
                                        if (!parsed) return null;
                                        const m = moment(parsed.toDate());
                                        return m.isValid() ? m : null;
                                    })()}
                                    onAccept={(newVal) => {
                                        if (!newVal || !newVal.isValid()) return;
                                        setDueDatePicking(false);
                                        setDueDateAnchor(null);
                                        handlePatchField('due_at', newVal.toISOString());
                                    }}
                                    ampm={false}
                                    slotProps={{
                                        textField: { size: 'small', sx: { width: 260 } },
                                        actionBar: { actions: ['cancel', 'accept'] },
                                    }}
                                />
                            </LocalizationProvider>
                        </Popover>

                        {/* Completed-date row */}
                        {isCompleted && task?.completed_at && (
                            <Box className="task-slideout-detail-row" data-testid="task-slideout-detail-row-completed" sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Iconify icon="eva:checkmark-circle-2-outline" width={15} />
                                </Box>
                                <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>Completed</Typography>
                                <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                                    {(() => {
                                        const actor = task.completed_by_practice_user || null;
                                        const completedByFullName = actor?.full_name
                                            || task.completed_by_full_name
                                            || (typeof task.completed_by === 'string' ? task.completed_by : '')
                                            || (task.completed_by && typeof task.completed_by === 'object'
                                                ? `${task.completed_by.first_name || ''} ${task.completed_by.last_name || ''}`.trim() || task.completed_by.email
                                                : '');
                                        const avatarUrl = actor?.avatar_url || task.completed_by_avatar_url || undefined;
                                        const initial = (completedByFullName || '?').charAt(0).toUpperCase() || '?';
                                        const primaryAssignee = assignees[0];
                                        const primaryName = primaryAssignee
                                            ? (primaryAssignee.get_full_name || `${primaryAssignee.first_name || ''} ${primaryAssignee.last_name || ''}`.trim() || primaryAssignee.email || '')
                                            : '';
                                        const showCompletedBy = completedByFullName && completedByFullName !== primaryName;
                                        return (
                                            <>
                                                {showCompletedBy && (
                                                    <Tooltip title={`Completed by ${completedByFullName}`}>
                                                        <Avatar src={avatarUrl} sx={{ width: 18, height: 18, fontSize: '0.6rem', flexShrink: 0 }}>
                                                            {initial}
                                                        </Avatar>
                                                    </Tooltip>
                                                )}
                                                <Typography sx={{ fontSize: '13px', color: 'success.main', fontWeight: 500, textAlign: 'right' }}>
                                                    {formatDue(task.completed_at)}
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                </Box>
                            </Box>
                        )}

                        {/* Created-by row */}
                        {task?.created_at && (
                            <Box className="task-slideout-detail-row" data-testid="task-slideout-detail-row-created-by" sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Iconify icon="eva:person-outline" width={15} />
                                </Box>
                                <Typography className="task-slideout-detail-label" data-testid="task-slideout-detail-label" sx={{ ...notesLabelStyle, width: propsLabelWidth, flexShrink: 0 }}>Created by</Typography>
                                <Box className="task-slideout-detail-value" data-testid="task-slideout-detail-value" sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {(() => {
                                        const isSystem = task.is_system_generated || (!task.created_by && task.template_id);
                                        const cb = task.created_by;
                                        const createdDate = (
                                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', ml: 'auto', flexShrink: 0 }}>
                                                {formatDue(task.created_at)}
                                            </Typography>
                                        );
                                        if (isSystem || !cb) {
                                            return (
                                                <>
                                                    <Tooltip title="System-generated task">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            <Iconify icon="eva:settings-2-outline" width={15} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                                                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 500 }}>System</Typography>
                                                        </Box>
                                                    </Tooltip>
                                                    {createdDate}
                                                </>
                                            );
                                        }
                                        const initials = ((cb.first_name?.[0] || '') + (cb.last_name?.[0] || '')).toUpperCase() || '?';
                                        const displayName = `${cb.first_name || ''} ${cb.last_name || ''}`.trim() || cb.email || '';
                                        // Resolve a profile picture from the already-loaded practice user list.
                                        const staffMatch = practiceUserResults.find((pu) => pu?.user?.id === cb.id);
                                        const avatarSrc = staffMatch?.user?.profile_picture || undefined;
                                        return (
                                            <>
                                                <Tooltip title={`Created by ${displayName}${cb.email && cb.email !== displayName ? ` · ${cb.email}` : ''}`}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, overflow: 'hidden' }}>
                                                        <Avatar src={avatarSrc} sx={{ width: 18, height: 18, fontSize: '0.6rem', flexShrink: 0 }}>
                                                            {initials}
                                                        </Avatar>
                                                        <Typography sx={{ fontSize: '13px', color: 'text.primary', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {displayName}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                                {createdDate}
                                            </>
                                        );
                                    })()}
                                </Box>
                            </Box>
                        )}

                        {/* Dependency row */}
                        {dep && (
                            <Box className="task-slideout-detail-row" data-testid="task-slideout-detail-row-dependency" sx={{ display: 'flex', alignItems: 'center', minHeight: 51, gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: depIsBlocking ? 'error.main' : 'text.disabled' }}>
                                    <Iconify icon="eva:lock-fill" width={15} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mb: 0.25 }}>Blocked by</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: '15px', color: depIsBlocking ? 'error.main' : 'text.secondary', fontWeight: 500 }}>
                                            {depTitle}
                                        </Typography>
                                        {depIsBlocking && (
                                            <Chip label="Blocked" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(error.main, 0.08), color: 'error.main', borderRadius: '6px' }} />
                                        )}
                                    </Box>
                                </Box>
                                <Tooltip title="Remove dependency">
                                    <IconButton size="small" onClick={handleRemoveDep} sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                        <Iconify icon="eva:close-circle-outline" width={14} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Set-dependency row */}
                        {!dep && !depPickerOpen && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Iconify icon="eva:link-2-outline" width={15} />
                                </Box>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setDepPickerOpen(true)}
                                    sx={{ p: 0, textTransform: 'none', fontSize: '13px', color: 'text.secondary', justifyContent: 'flex-start', fontWeight: 400, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                                >
                                    Set dependency
                                </Button>
                            </Box>
                        )}

                        {/* Dependency picker */}
                        {depPickerOpen && (
                            <Box sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Autocomplete
                                    size="small"
                                    options={depOptions}
                                    getOptionLabel={(opt) => opt.title || opt.name || `Task #${opt.id}`}
                                    loading={depSearchLoading}
                                    onInputChange={(_, val) => handleDepSearch(val)}
                                    onChange={(_, val) => handleSetDep(val)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Search tasks…"
                                            autoFocus
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {depSearchLoading && <MuiCircularProgress size={14} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    sx={{ mb: 0.5 }}
                                />
                                <Button size="small" variant="text" onClick={() => setDepPickerOpen(false)} sx={{ p: 0, textTransform: 'none', fontSize: '0.75rem', color: 'text.disabled' }}>
                                    Cancel
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ---- Notes (minimal pen-icon) ---- */}
                <Box className="task-slideout-notes-section" data-testid="task-slideout-notes-section" sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <Iconify icon="eva:file-text-outline" width={13} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                            Notes
                        </Typography>
                    </Box>
                    {notesEditing ? (
                        <ClickAwayListener
                            onClickAway={() => {
                                if (notesDirty) handleSaveNotes();
                                setNotesEditing(false);
                            }}
                        >
                            <Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    autoFocus
                                    minRows={3}
                                    data-testid="task-notes-editor"
                                    placeholder="Add notes for this task…"
                                    value={notes}
                                    onChange={(e) => {
                                        setNotes(e.target.value);
                                        setNotesDirty(true);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                            e.preventDefault();
                                            // Restore the same description-then-note
                                            // fallback the initial-load uses; cancelling
                                            // an edit must not blank out migrated
                                            // V1 ``note`` content.
                                            setNotes(task?.description || task?.note || '');
                                            setNotesDirty(false);
                                            setNotesEditing(false);
                                        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                            e.preventDefault();
                                            if (notesDirty) handleSaveNotes();
                                            setNotesEditing(false);
                                        }
                                    }}
                                    variant="standard"
                                    InputProps={{ disableUnderline: false }}
                                    sx={{
                                        '& .MuiInput-root': {
                                            fontSize: '13px',
                                            '&:before': { borderBottomColor: 'divider' },
                                            '&:after': { borderBottomColor: 'primary.main' },
                                        },
                                    }}
                                />
                                {savingNotes && (
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.5 }}>Saving…</Typography>
                                )}
                            </Box>
                        </ClickAwayListener>
                    ) : (
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-label={notes ? 'Edit notes' : 'Add notes'}
                            data-testid="task-notes-display"
                            onClick={() => setNotesEditing(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setNotesEditing(true);
                                }
                            }}
                            sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 0.75,
                                py: 0.5, cursor: 'pointer', borderRadius: '6px',
                                '&:hover .notes-pen-icon': { opacity: 1, color: 'primary.main' },
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <Typography
                                sx={{
                                    flex: 1, fontSize: '13px',
                                    color: notes ? 'text.primary' : 'text.disabled',
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                    fontStyle: notes ? 'normal' : 'italic',
                                }}
                            >
                                {notes ? interpolateTaskMergeTags(notes, task) : 'Add notes…'}
                            </Typography>
                            <Iconify
                                className="notes-pen-icon"
                                icon="eva:edit-2-outline"
                                width={14}
                                sx={{ mt: 0.25, opacity: 0.45, color: 'text.disabled', transition: 'opacity 0.15s, color 0.15s', flexShrink: 0 }}
                            />
                        </Box>
                    )}
                </Box>

                {/* ---- Comments ---- */}
                <Box className="task-slideout-comments-section" data-testid="task-slideout-comments-section" sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                        <Iconify icon="eva:message-square-outline" width={13} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                            Comments
                        </Typography>
                    </Box>
                    <TaskDetailComments
                        comments={comments}
                        practiceId={String(practiceId)}
                        taskId={String(taskId)}
                        // V1→V2 parity: surface the lead-scoped LeadNote
                        // stream alongside task-scoped TaskComment rows
                        // so the V1 "Comments" history survives in the V2
                        // sidebar. ``leadNotes`` is normalised + merged
                        // inside TaskDetailComments to avoid double-source
                        // logic at every render site.
                        leadNotes={leadNotes}
                        leadId={leadIdForNotes ? String(leadIdForNotes) : null}
                        onRefresh={() => {
                            if (practiceId && taskId) dispatch(getComments(practiceId, taskId));
                            if (leadIdForNotes && practiceId) {
                                dispatch(getLeadNotes(leadIdForNotes, practiceId));
                            }
                        }}
                    />
                </Box>

                {/* Visual separator between Comments and Attachments — they're distinct concepts */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 2 }} />

                {/* ---- Attachments (collapsed by default) ---- */}
                <Accordion className="task-slideout-attachments-section" data-testid="task-slideout-attachments-section" sx={accordionSectionStyle} disableGutters>
                    <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" width={16} sx={{ color: 'text.disabled' }} />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Iconify icon="eva:attach-2-outline" width={13} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                                Attachments
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TaskAttachments
                            practiceId={String(practiceId)}
                            taskId={String(taskId)}
                            leadId={task?.lead?.id ? String(task.lead.id) : null}
                            checklist={checklist}
                            open={open !== false}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* ---- Time tracking (collapsed by default) ---- */}
                <Accordion className="task-slideout-time-tracker" data-testid="task-slideout-time-tracker" sx={accordionSectionStyle} disableGutters>
                    <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" width={16} sx={{ color: 'text.disabled' }} />}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            Time tracking
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TaskTimeTracker
                            practiceId={String(practiceId)}
                            taskId={String(taskId)}
                            task={task}
                            onRefresh={handleRefresh}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* Debug ID footer */}
                {task?.id && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider', opacity: 0.6 }}>
                        <DebugIdBadge
                            taskId={task.id}
                            leadId={task?.lead?.id}
                            practiceId={practiceId}
                        />
                        {isSuperuser && sourceTemplateId && (
                            <Box
                                component="span"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(PATH_DASHBOARD.task.tasks.templateEdit(practiceId, sourceTemplateId));
                                }}
                                sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    cursor: 'pointer',
                                    color: 'text.disabled',
                                    fontFamily: 'monospace',
                                    fontSize: '0.68rem',
                                    lineHeight: 1,
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    '&:hover': { color: 'primary.main' },
                                }}
                                data-testid="task-source-template-link"
                            >
                                View source template #{sourceTemplateId}
                            </Box>
                        )}
                        {isSuperuser && !sourceTemplateId && migratedFromReminderId && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    color: 'text.disabled',
                                    fontFamily: 'monospace',
                                    fontSize: '0.68rem',
                                    lineHeight: 1,
                                }}
                                data-testid="task-migrated-from-reminder"
                            >
                                Migrated from V1 reminder #{migratedFromReminderId}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* #318 — Follow-up popups (status-routed, mirrors V1 TaskReminder) */}
            {openContactLead && (
                <ContactLeadPopupRouter
                    open={openContactLead}
                    headerButtons
                    practiceId={practiceId}
                    onClose={handleFollowUpPopupClose}
                    user={rowLead}
                    leadTreatmentId={rowLead?.treatment_id || null}
                    steps={leadStep}
                    debugSource={contactLeadDebugSource}
                    BookingAttendance={BookingAttendance}
                    type="followup"
                    validStep={validStep}
                    updateStep={() => setValidStep(!validStep)}
                    inputRef={inputRef}
                    CreateLeadHeaderData={CreateLeadHeaderData}
                    ContactedCount={rowLead?.lead_treatments__lead_contacted_counts__count}
                />
            )}
            {openAttended && (
                <OpenAttendedPopup
                    open={openAttended}
                    isOverview
                    headerButtons
                    onClose={handleFollowUpPopupClose}
                    handleNoAttended={handleNoAttendedFromAttendedPopup}
                    user={rowLead}
                    type="recordAttended"
                    inputRef={inputRef}
                    CreateLeadHeaderData={CreateLeadHeaderData}
                />
            )}
            {openUnAttended && (
                <OpenUnAttendedPopup
                    appointmentSelection={appointmentOutcomeSelection}
                    open={openUnAttended}
                    headerButtons
                    onClose={handleFollowUpPopupClose}
                    user={rowLead}
                    type="recordUnAttended"
                    inputRef={inputRef}
                    CreateLeadHeaderData={CreateLeadHeaderData}
                    handleCloseAndReload={handleFollowUpPopupClose}
                    archiveContext={{
                        practiceId,
                        leadId: rowLead?.lead_id || task?.lead?.id || null,
                        leadTreatmentId: rowLead?.treatment_id || task?.lead_treatment?.id || null,
                        source: 'tasks-v2:fta-sidebar-popup',
                    }}
                    onBack={() => { dispatch(updateFollowUpLead('Attended')); setOpenUnAttended(false); setOpenAttended(true); }}
                />
            )}
            {openTreatmentHub && (
                <CompletedTreatmentHub
                    open={openTreatmentHub}
                    handleClose={closeTreatmentHub}
                    handleYes={handleTreatmentHubYes}
                    row={hubRow}
                    practiceId={practiceId}
                    isLoader={isLoader}
                />
            )}
        </>
    );
}

TaskBodyView.propTypes = {
    task: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string,
        priority: PropTypes.string,
        category: PropTypes.string,
        due_at: PropTypes.string,
        completed_at: PropTypes.string,
        resume_at: PropTypes.string,
        is_overdue: PropTypes.bool,
        assignments: PropTypes.array,
        checklist: PropTypes.array,
        checklist_items: PropTypes.array,
        depends_on: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
        lead: PropTypes.object,
        lead_treatment: PropTypes.object,
        completed_by: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        completed_by_full_name: PropTypes.string,
        completed_by_avatar_url: PropTypes.string,
        completed_by_practice_user: PropTypes.object,
        comments: PropTypes.array,
    }),
    lead: PropTypes.object,
    leadTreatment: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    mode: PropTypes.oneOf(['sidebar', 'detail', 'modal']),
    open: PropTypes.bool,
    onTaskUpdated: PropTypes.func,
    onClose: PropTypes.func,
    nowTick: PropTypes.number,
    pushItemUndo: PropTypes.func,
};

TaskBodyView.defaultProps = {
    task: null,
    lead: null,
    leadTreatment: null,
    mode: 'sidebar',
    open: true,
    onTaskUpdated: null,
    onClose: null,
    nowTick: 0,
    pushItemUndo: null,
};
